import { useQuery } from '@tanstack/react-query';

import { warehouseApi } from 'api/warehouse';

export const FACILITIES_QUERY_KEY = 'facilities';

/**
 * Fetches the list of facilities available to procurement workflows.
 *
 * The query normalizes facility responses into option objects compatible with select and multi-select
 * components. Results are cached for 15 minutes to avoid redundant network calls across the app.
 *
 * @param {Object} [options]
 * @param {boolean} [options.activeOnly=true] - Limits results to active facilities when true.
 * @param {boolean} [options.enabled=true] - Enables the query when true.
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<{label:string,value:string|number}>, Error>}
 */
export const useFacilitiesQuery = ({ activeOnly = true, enabled = true } = {}) =>
    useQuery({
        queryKey: [FACILITIES_QUERY_KEY, activeOnly ? 'active' : 'all'],
        enabled,
        queryFn: async () => {
            const response = await warehouseApi.getFacilities(activeOnly);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch facilities');
            }

            const rows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
            return rows.filter((row) => row?.id !== null && row?.id !== undefined);
        },
        staleTime: 15 * 60_000,
        gcTime: 30 * 60_000,
    });
